import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import api from '../../services/api';

const ImportClients = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'application/json') {
            setFile(selectedFile);
            setError(null);
        } else {
            setError('Please select a valid JSON file');
            setFile(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        try {
            setLoading(true);
            setError(null);

            // Read the file content
            const fileContent = await file.text();
            const clients = JSON.parse(fileContent);

            // Process clients to extract only needed fields
            const processedClients = clients.map(client => ({
                clientId: client.ClientID,
                firstName: client.FirstName,
                lastName: client.LastName,
                ssUsername: client.SSUsername,
                isCoachingOrPlaying: client.Coaching_Playing?.toLowerCase().includes('yes') ? true : undefined
            }));

            // Send to backend
            const response = await api.post('/admin/clients/import', {
                clients: processedClients
            });

            setResult(response.data);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to import clients');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Import Clients
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                    Upload a JSON file containing client information. The file should contain an array of clients with the following fields:
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemText primary="ClientID" secondary="EZFacility client ID" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="FirstName" secondary="Client's first name" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="LastName" secondary="Client's last name" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="SSUsername" secondary="Client's username" />
                    </ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        component="label"
                        disabled={loading}
                    >
                        Select File
                        <input
                            type="file"
                            hidden
                            accept="application/json"
                            onChange={handleFileChange}
                        />
                    </Button>
                    {file && (
                        <Typography variant="body2" color="textSecondary">
                            Selected: {file.name}
                        </Typography>
                    )}
                </Box>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {file && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                        sx={{ mt: 2 }}
                    >
                        Upload and Import
                    </Button>
                )}
                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Successfully imported {result.imported} clients.
                        {result.updated > 0 && ` Updated ${result.updated} existing clients.`}
                        {result.skipped > 0 && ` Skipped ${result.skipped} duplicate entries.`}
                        {result.skippedNoUsername > 0 && ` Skipped ${result.skippedNoUsername} entries without username.`}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default ImportClients; 